import type { ReactNode } from "react";

import type { IReviewProviderScoreData } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

export interface IReviewProviderCardProps {
  data: IReviewProviderScoreData;
  /**
   * Platform name exposed as screen-reader/crawler text. Pass the logo as
   * decorative (aria-hidden) alongside it so the name is announced once.
   */
  providerName?: string;
  logo?: ReactNode;
  stars?: ReactNode;
  gradeLabel?: string;
  reviewCountText?: string;
  className?: string;
  /** Single horizontal row (logo, stars, score, reviews). Default keeps the stacked two-row layout. */
  layout?: "stacked" | "inline";
}

export const ReviewProviderCard = ({
  data,
  providerName,
  logo,
  stars,
  gradeLabel,
  reviewCountText,
  className,
  layout = "stacked",
}: IReviewProviderCardProps) => {
  const scoreText = `${data.score} / ${data.maxScore}`;
  const srProviderName = providerName ? (
    <span className="sr-only">{`${providerName} `}</span>
  ) : null;
  const scoreBlock = (
    <div className="text-base-strong">
      {gradeLabel ? `${gradeLabel} ${scoreText}` : scoreText}
    </div>
  );

  if (layout === "inline") {
    return (
      <div
        className={cn(
          "flex min-h-0 flex-row flex-wrap items-center justify-center gap-x-2 gap-y-0 md:justify-start",
          className,
        )}
      >
        {srProviderName}
        {logo}
        <span className="inline-flex shrink-0 items-center">{stars}</span>
        {/* Keep the rating and review count as one non-wrapping unit so they
            stay on the same line and drop to the second line together when the
            block wraps on narrow screens. */}
        <div className="flex items-center gap-x-2 whitespace-nowrap">
          {scoreBlock}
          {reviewCountText && (
            <div className="flex gap-x-1.5">
              <span className="text-base-strong">|</span>
              <div className="text-base-strong">{reviewCountText}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-full flex-col justify-end gap-y-1", className)}>
      <div className="flex items-center justify-center gap-x-3">
        {srProviderName}
        {logo}
        {stars}
      </div>
      <div className="flex items-center justify-center gap-x-3 whitespace-nowrap">
        {scoreBlock}
        {reviewCountText && (
          <div className="text-base-strong">{reviewCountText}</div>
        )}
      </div>
    </div>
  );
};
